/**
 * Чистая логика вкладки стримеров: разложить онлайн-стримеров по командам.
 * Вынесена из компонента, чтобы покрыть тестом без React.
 *
 * Источник составов — ПУБЛИЧНЫЙ /tournaments/{id}/teams (draft-эндпоинт
 * /draft/{id}/teams под [Authorize] — анонимной аудитории стримов он бы отдал 401).
 * У публичного DTO капитан УЖЕ входит в members (финализированный ростер из
 * team_members), поэтому добавлять его отдельно не нужно — помечаем по captainId.
 */

export interface StreamStatusInput {
  userId: string;
  twitchLogin: string;
  isLive: boolean;
}

export interface TeamMemberInput {
  userId: string;
  displayName: string;
}

export interface TeamRosterInput {
  id: string;
  name: string;
  /** userId капитана; пусто — капитан не отмечен. Капитан входит в members. */
  captainId: string;
  members: TeamMemberInput[];
}

export interface LiveStreamer {
  userId: string;
  twitchLogin: string;
  displayName: string;
  isCaptain: boolean;
}

export interface TeamGroup {
  teamId: string;
  teamName: string;
  streamers: LiveStreamer[];
}

export interface StreamerGroups {
  groups: TeamGroup[];
  /** Живые участники, не привязанные ни к одной команде (край: состав неполный). */
  others: LiveStreamer[];
}

export function buildStreamerGroups(
  playerStreams: readonly StreamStatusInput[],
  teams: readonly TeamRosterInput[],
): StreamerGroups {
  // userId → twitch login только для тех, кто СЕЙЧАС в эфире.
  const liveByUserId = new Map<string, string>();
  for (const s of playerStreams) {
    if (s.isLive && s.userId && s.twitchLogin) {
      liveByUserId.set(s.userId, s.twitchLogin);
    }
  }

  const claimed = new Set<string>();
  const groups: TeamGroup[] = [];

  for (const team of teams) {
    const seen = new Set<string>();
    const streamers: LiveStreamer[] = [];
    for (const m of team.members) {
      if (!m.userId || seen.has(m.userId)) continue;
      seen.add(m.userId);
      const login = liveByUserId.get(m.userId);
      if (!login) continue;
      streamers.push({
        userId: m.userId,
        twitchLogin: login,
        displayName: m.displayName,
        isCaptain: Boolean(team.captainId) && m.userId === team.captainId,
      });
      claimed.add(m.userId);
    }

    // Пустые команды (никто не в эфире) не показываем.
    if (streamers.length > 0) {
      groups.push({ teamId: team.id, teamName: team.name, streamers });
    }
  }

  const others: LiveStreamer[] = [];
  for (const [userId, login] of liveByUserId) {
    if (!claimed.has(userId)) {
      others.push({
        userId,
        twitchLogin: login,
        displayName: login,
        isCaptain: false,
      });
    }
  }

  return { groups, others };
}
